import { useState } from "react";
import { Section, SectionRow } from "@/components/Section";
import { CenteredError, Spinner } from "@/components/Spinner";
import { Icon } from "@/components/Icon";
import { BackNav } from "@/components/BackNav";
import { useReferrals, useReferralsPaginated } from "@/api/hooks";
import { ExchangeSubscriptionModal } from "@/components/ExchangeSubscriptionModal";
import { WithdrawOfferModal } from "@/components/WithdrawOfferModal";
import { WithdrawCardModal } from "@/components/WithdrawCardModal";
import { ReferralLinksSection } from "@/components/ReferralLinks";
import { tg } from "@/lib/telegram";
import { showToast } from "@/lib/toast";
import { showAppAlert } from "@/lib/appAlert";
import { formatAmount, formatDate } from "@/lib/utils";
import { formatUahSigned, referralDebtText } from "@/lib/referralDebt";

/**
 * Реферали — баланс + посилання + список запрошених.
 *
 * Економіка (BL §8.2 / 1.0 about_ref_text texts.py:3381):
 *   - +2 ₴ за реєстрацію реферала — після його підписки на канал
 *     (owner 2026-10-11)
 *   - +297 ₴ за його першу платну підписку (30%)
 *   - +147 ₴ за кожне продовження довічно (15%)
 */
export function ReferralsPage() {
  const refQuery = useReferrals();
  const [exchangeOpen, setExchangeOpen] = useState(false);

  if (refQuery.isLoading) return <Spinner />;
  if (refQuery.isError) return <CenteredError message={refQuery.error.message} />;

  const r = refQuery.data!;

  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(r.referral_link);
      tg()?.HapticFeedback.notificationOccurred("success");
      showToast("Посилання скопійовано", { emoji: "✓" });
    }
  };

  return (
    <>
      {/* Уніфікований хедер: логотип зліва + «Меню» справа — як на головній,
          як на КОЖНІЙ сторінці (юзер 2026-06-10). Раніше тут був кастомний
          навбар без логотипа і «Меню» зліва. */}
      <BackNav />

      {/* Hero: eyebrow на тій самій відстані від логотипа (BackNav mb-4 +
          pt-4 = 32px), як на всіх сторінках (юзер 2026-06-15). */}
      <div className="relative mb-6 pt-4">
        <div className="flex items-center gap-2 font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-ink-dim">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          Реферальна програма
        </div>
        <h1
          className="mt-2 font-display font-bold text-ink"
          style={{ fontSize: "1.625rem", lineHeight: 1.1, letterSpacing: "-0.02em" }}
        >
          Запрошуй —{" "}
          <span className="accent-italic" style={{ fontSize: "1.75rem", fontWeight: 600 }}>
            заробляй.
          </span>
        </h1>
      </div>

      {/* Порядок блоків (юзер 2026-06-11): 1) посилання, 2) як це працює,
          3) баланс+вивід, 4) запрошені (нижче). */}

      {/* 1. Реферальне посилання — перший блок. Об'єднана картка:
          посилання + кнопка копіювати знизу клеїться до краю. */}
      <div className="mb-6 overflow-hidden rounded-ios-card bg-surface shadow-card-soft">
        <div className="px-4 pb-3 pt-4">
          <div className="font-mono text-[0.65625rem] uppercase tracking-[0.08em] text-ink-dim">
            Твоє реферальне посилання
          </div>
          <div className="mt-2 break-all font-mono text-[0.75rem] leading-relaxed text-ink">
            {r.referral_link}
          </div>
        </div>
        <button
          type="button"
          onClick={handleCopyLink}
          className="flex w-full items-center justify-center gap-2 bg-primary py-3 font-display text-[0.8125rem] font-semibold text-white active:scale-[0.98] transition"
        >
          <Icon name="doc" size={14} color="#fff" />
          Копіювати посилання
        </button>
      </div>

      {/* Мої посилання (owner 2026-10-25) — лише для людей із прапорцем
          `referral_links`; в інших блок не рендериться зовсім. */}
      {r.links_enabled && <ReferralLinksSection />}

      {/* 2. Як це працює — economy table (як в 1.0 about_ref_text) */}
      <Section title="Як це працює">
        <RewardRow
          isFirst
          amount="+2 ₴"
          label="За реєстрацію друга"
          hint="Коли він підпишеться на наш канал"
        />
        <RewardRow
          amount="+297 ₴"
          label="За першу підписку друга"
          hint="30% від тарифу 990 ₴/міс."
        />
        <RewardRow
          isLast
          amount="+147 ₴"
          label="За кожне продовження"
          hint="15% довічно, поки платить"
        />
      </Section>

      {/* 3. Баланс + вивід — 3 колонки, перша (баланс) primary акцент.
          Баланс у мінусі після чарджбеку друга (owner 2026-10-22) — без
          акценту, з мінусом, а під карткою пояснення, звідки він. */}
      <Section
        footer={
          r.balance_uah < 0 ? (
            <span className="block [text-wrap:balance]">
              {referralDebtText(r.balance_uah)}
            </span>
          ) : undefined
        }
      >
        <div className="grid grid-cols-3 divide-x divide-line">
          <StatCol
            label="Баланс"
            value={formatUahSigned(r.balance_uah)}
            accent={r.balance_uah >= 0}
          />
          <StatCol
            label="Зароблено"
            value={`${formatAmount(r.total_earned)}\u00A0₴`}
          />
          <StatCol
            label="Рефералів"
            value={String(r.referrals_count)}
          />
        </div>
        {/* Дії з балансом (owner 2026-07-24): дві окремі кнопки-картки (стек,
            бо різні за довжиною). Обмін — акцентна (вигідніше за вивід). */}
        <div className="border-t border-line p-3 space-y-2">
          <button
            type="button"
            onClick={() => {
              tg()?.HapticFeedback?.impactOccurred?.("medium");
              setExchangeOpen(true);
            }}
            className="w-full flex items-center gap-3 rounded-2xl bg-primary/[0.06] ring-1 ring-primary/25 p-3 text-left transition active:scale-[0.99]"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary text-white shadow-card-soft">
              <Icon name="crown" size={16} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-[0.875rem] font-bold text-ink">
                Обміняти на підписку
              </div>
              <div className="text-[0.71875rem] text-ink-dim leading-snug [text-wrap:balance]">
                Обмінюй зароблені гроші на підписку за вигідною ціною
              </div>
            </div>
            <span className="text-primary shrink-0">
              <Icon name="arrow-right" size={16} />
            </span>
          </button>

          <WithdrawButton
            balance={r.balance_uah}
            onExchange={() => setExchangeOpen(true)}
          />
        </div>
      </Section>

      {/* Recent referrals — paginated «Завантажити ще» */}
      <ReferralsListSection totalCount={r.referrals_count} />

      {exchangeOpen && (
        <ExchangeSubscriptionModal onClose={() => setExchangeOpen(false)} />
      )}
    </>
  );
}

function ReferralsListSection({ totalCount }: { totalCount: number }) {
  const q = useReferralsPaginated(10);
  const allItems = (q.data?.pages ?? []).flatMap((p) => p.items);

  return (
    <Section
      title={`Запрошені (${totalCount})`}
      footer={
        totalCount === 0
          ? "Ще немає жодного запрошеного — поділись посиланням з друзями."
          : undefined
      }
    >
      {totalCount === 0 ? (
        <div className="p-4 text-[0.8125rem] text-ink-dim">
          Список з'явиться щойно прийде перший друг.
        </div>
      ) : (
        <>
          {allItems.map((u, i) => (
            <SectionRow
              key={u.user_id}
              trailing={
                <span className="font-mono text-[0.65625rem] uppercase tracking-[0.08em] text-ink-dim">
                  {formatDate(u.created_at)}
                </span>
              }
            >
              <div className="flex items-center gap-3">
                <div className="font-mono text-[0.6875rem] tabular-nums text-ink-dim">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="grid h-8 w-8 place-items-center rounded-[0.625rem] bg-surface-soft text-primary">
                  <Icon name="user" size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-[0.875rem] font-semibold text-ink">
                    {u.first_name ??
                      (u.tg_username ? `@${u.tg_username}` : `User ${u.user_id}`)}
                  </div>
                  {u.tg_username && u.first_name && (
                    <div className="text-[0.6875rem] text-ink-dim">
                      @{u.tg_username}
                    </div>
                  )}
                </div>
              </div>
            </SectionRow>
          ))}
          {q.hasNextPage && (
            <button
              type="button"
              onClick={() => q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
              className="w-full py-3 text-center font-display text-[0.8125rem] font-semibold text-primary active:text-primary-deep disabled:text-ink-dim"
            >
              {q.isFetchingNextPage
                ? "Завантаження…"
                : `Завантажити ще (${totalCount - allItems.length})`}
            </button>
          )}
        </>
      )}
    </Section>
  );
}

function StatCol({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  // Owner 2026-07-27: «₴ не має переноситись». На великих балансах
  // (62 503 ₴) гривня зривалась на другий рядок — тому nowrap, а щоб довге
  // число не вилазило за колонку, дрібнимо шрифт за довжиною рядка.
  const fontSize = value.length > 11 ? 13 : value.length > 8 ? 15 : 18;
  return (
    <div className="px-3 py-3 text-center">
      <div className="font-mono text-[0.59375rem] uppercase tracking-[0.08em] text-ink-dim">
        {label}
      </div>
      <div
        className={`mt-1 font-display font-bold tabular-nums whitespace-nowrap ${
          accent ? "text-primary" : "text-ink"
        }`}
        style={{ fontSize, lineHeight: 1.1, letterSpacing: "-0.02em" }}
      >
        {value}
      </div>
    </div>
  );
}

function RewardRow({
  amount,
  label,
  hint,
  isFirst,
  isLast,
}: {
  amount: string;
  label: string;
  hint: string;
  isFirst?: boolean;
  isLast?: boolean;
}) {
  const _ = isFirst;
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 ${
        isLast ? "" : "border-b border-line"
      }`}
    >
      <div className="font-display text-[1.125rem] font-bold text-primary tabular-nums w-[4.875rem] shrink-0">
        {amount}
      </div>
      <div className="flex-1">
        <div className="font-display text-[0.8125rem] font-semibold text-ink">
          {label}
        </div>
        <div className="mt-0.5 text-[0.6875rem] text-ink-dim">{hint}</div>
      </div>
    </div>
  );
}

function WithdrawButton({
  balance,
  onExchange,
}: {
  balance: number;
  onExchange: () => void;
}) {
  const [offerOpen, setOfferOpen] = useState(false);
  // Owner 2026-10-19 («як у Findly»): після «Все одно вивести на картку» —
  // крок із номером картки, заявка йде вже з ним.
  const [cardOpen, setCardOpen] = useState(false);

  // Owner 2026-07-27: перед виводом показуємо попап з порівнянням — обмін на
  // підписку вигідніший за вивід. Замість системного showConfirm: там не
  // покажеш ні днів, ні вигоди. Сам вивід лишається в попапі другою кнопкою.
  const handleClick = () => {
    tg()?.HapticFeedback?.impactOccurred?.("medium");
    if (balance < 0) {
      // Мінус після чарджбеку (owner 2026-10-22): пояснюємо саме його, а не
      // «мінімальна сума 100 ₴».
      showAppAlert(referralDebtText(balance));
      return;
    }
    if (balance < 100) {
      showAppAlert(
        `Мінімальна сума для виводу — 100 ₴. Зараз на реферальному балансі ${formatAmount(balance)} ₴.`,
      );
      return;
    }
    setOfferOpen(true);
  };

  return (
    <>
      {offerOpen && (
        <WithdrawOfferModal
          balance={balance}
          onExchange={() => {
            setOfferOpen(false);
            onExchange();
          }}
          onWithdraw={() => {
            setOfferOpen(false);
            setCardOpen(true);
          }}
          onClose={() => setOfferOpen(false)}
        />
      )}
      {cardOpen && (
        <WithdrawCardModal balance={balance} onClose={() => setCardOpen(false)} />
      )}
      <button
        type="button"
        onClick={handleClick}
        className="w-full flex items-center gap-3 rounded-2xl bg-bg/60 ring-1 ring-ink/[0.06] p-3 text-left transition active:scale-[0.99] disabled:opacity-60"
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-ink/[0.06] text-ink-dim">
          <Icon name="arrow-right" size={16} className="-rotate-45" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[0.875rem] font-bold text-ink">
            Вивести на картку
          </div>
          <div className="text-[0.71875rem] text-ink-dim leading-snug">
            Виведи зароблені гроші на картку
          </div>
        </div>
        <span className="text-ink/25 shrink-0">
          <Icon name="arrow-right" size={16} />
        </span>
      </button>
    </>
  );
}
